import { useEffect, useState } from 'react';
import useAxiosAuth from './useAxiosAuth';
import baseURL from '../utils/baseURL';
import { setCurrentOrg, ORG_CACHE_KEY } from '../utils/orgWords';

// What the CURRENT user's hospital has switched on.
//
// The sidebar and pages use this to hide what a hospital doesn't have. Hiding is
// a courtesy only — the server refuses the same features via
// requireTenantFeature, so a hidden menu is not the control, just the polite
// half of it.
//
// Cached in localStorage and served immediately on the next load, because a
// menu that appears and then re-arranges a moment later reads as a glitch. The
// network answer replaces it as soon as it lands.
const CACHE_KEY = 'tenantFeatures';
// Separate from CACHE_KEY on purpose — isOpenPlatform fails CLOSED (see
// below), so it cannot share a "missing means allow-all" cache with features.
const OPEN_PLATFORM_CACHE_KEY = 'tenantIsOpenPlatform';
// 'connected' | 'isolated'. Decides which patient-table actions the desk
// gets (isolated: tenant logins are real → password reset shows). Defaults
// to 'connected' — the safer read: it HIDES the password button rather than
// offering a reset that means nothing.
const CONNECTION_MODE_CACHE_KEY = 'tenantConnectionMode';
// Where the tenant's plan from the platform stands (doc/5.10.2026.md) —
// { kind: 'no_fee'|'none'|'active'|'ended', action, endsOn, daysLeft,
// endingSoon, endedOn }, dates only. Drives the renewal banners and whether
// Settings shows a Subscription tab. null = not known (no banner).
const BILLING_CACHE_KEY = 'tenantBilling';

// FAILS OPEN. If the call fails we show everything rather than presenting an
// empty sidebar — a hidden menu is cosmetic, and the API still enforces the
// real rule.
const ALLOW_ALL = new Proxy({}, { get: () => true });

const readCache = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const readOpenPlatformCache = () => {
    try {
        return localStorage.getItem(OPEN_PLATFORM_CACHE_KEY) === 'true';
    } catch {
        return false;
    }
};

export default function useTenantFeatures() {
    const [features, setFeatures] = useState(() => readCache());
    // FAILS CLOSED, unlike `features` above — this is not a hide-if-off
    // convenience, it gates a real cross-tenant identity screen
    // (Backend/api/middleware/requireAdmin.js: requireOpenPlatformAdmin
    // re-checks it server-side regardless, but the menu must default to "no"
    // too, not "everything" like an ordinary feature flag would).
    const [isOpenPlatform, setIsOpenPlatform] = useState(() => readOpenPlatformCache());
    const [connectionMode, setConnectionMode] = useState(() => {
        try { return localStorage.getItem(CONNECTION_MODE_CACHE_KEY) || 'connected'; }
        catch { return 'connected'; }
    });
    const [billing, setBilling] = useState(() => {
        try { return JSON.parse(localStorage.getItem(BILLING_CACHE_KEY) || 'null'); }
        catch { return null; }
    });

    useEffect(() => {
        // Nobody signed in yet — nothing to scope to.
        if (localStorage.getItem('isAuthenticated') !== 'true') return;

        let cancelled = false;
        (async () => {
            try {
                const axiosInstance = useAxiosAuth();
                const res = await axiosInstance.get(`${baseURL}/tenants/my-features`);
                if (cancelled) return;
                const next = res?.data?.features || null;
                if (next) {
                    setFeatures(next);
                    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
                }
                const openPlatform = !!res?.data?.isOpenPlatform;
                setIsOpenPlatform(openPlatform);
                localStorage.setItem(OPEN_PLATFORM_CACHE_KEY, String(openPlatform));
                // Only the two known values are stored — an older backend
                // that doesn't send it leaves the safe 'connected' default.
                if (res?.data?.connectionMode === 'isolated' || res?.data?.connectionMode === 'connected') {
                    setConnectionMode(res.data.connectionMode);
                    localStorage.setItem(CONNECTION_MODE_CACHE_KEY, res.data.connectionMode);
                }
                // The tenant's KIND (hospital / clinic / moc / organisation) and
                // name — every translation reads it (utils/orgWords.js via the
                // i18n post-processor) so "hospital" becomes the right words.
                // An older backend sends no `org`: nothing is stored and the
                // words stay "hospital", exactly as before.
                const nextBilling = res?.data?.billing;
                if (nextBilling && typeof nextBilling === 'object') {
                    setBilling(nextBilling);
                    try { localStorage.setItem(BILLING_CACHE_KEY, JSON.stringify(nextBilling)); } catch { /* private mode */ }
                } else {
                    setBilling(null);
                    try { localStorage.removeItem(BILLING_CACHE_KEY); } catch { /* private mode */ }
                }
                const org = res?.data?.org;
                if (org && typeof org === 'object') {
                    setCurrentOrg(org);
                    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({
                        kind: org.kind ?? null, typeName: org.typeName ?? null, name: org.name ?? null,
                    }));
                } else {
                    setCurrentOrg(null);
                    localStorage.removeItem(ORG_CACHE_KEY);
                }
            } catch {
                // Keep whatever is cached; the guard below falls back to
                // allow-all when there is nothing.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const map = features || ALLOW_ALL;

    return {
        features: map,
        // Unknown ids return true: a feature that isn't in the catalogue yet is
        // not something this hospital has opted out of.
        has: (id) => (id ? map[id] !== false : true),
        isOpenPlatform,
        connectionMode,
        isIsolated: connectionMode === 'isolated',
        billing,
    };
}
